/**
 * Dashboard Filter Header
 * Filter controls for dashboard (Currency + Date Reference)
 * Uses Dashboard context instead of React Hook Form
 */

import { useMemo } from 'react';
import type { InvoiceSearchRequestDto } from 'src/generated/services/TFinancialApi';
import { DashboardCurrencyDropdown } from './DashboardCurrencyDropdown';
import { DashboardDateDropdown } from './DashboardDateDropdown';
import { useDashboard } from '../../pages/dashboardPage/DashboardContext';
import { AuditDashboardTour } from 'src/presentations/guided-tours/tours/AuditDashboardTour';

export const DashboardFilterHeader = ({ tour }: { tour: AuditDashboardTour }) => {
  // Get data and handlers from Dashboard context
  const { filters, allowedCurrencies, onFilterChange, isLoadingInvoiceData } = useDashboard();

  // Transform allowed currencies array to dropdown options
  const currencyOptions = useMemo(
    () =>
      allowedCurrencies.map((currency) => ({
        label: currency,
        value: currency,
      })),
    [allowedCurrencies]
  );

  // Handle currency change
  const handleCurrencyChange = (value: string) => {
    onFilterChange({ currencyReference: value });
  };

  // Handle date reference change
  const handleDateChange = (value: InvoiceSearchRequestDto['dateReference']) => {
    onFilterChange({ dateReference: value });
  };

  return (
    <div className='flex items-center gap-12 xl:gap-16'>
      <DashboardCurrencyDropdown
        id={tour.registerStep('currencyReference')}
        value={filters.currencyReference}
        options={currencyOptions}
        onChange={handleCurrencyChange}
        disabled={isLoadingInvoiceData}
      />
      <DashboardDateDropdown
        id={tour.registerStep('dateReference')}
        value={filters.dateReference}
        onChange={handleDateChange}
        disabled={isLoadingInvoiceData}
      />
    </div>
  );
};
